import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  AuthenticationResult,
} from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { useQueryClient } from '@tanstack/react-query';

export type User = {
  email: string;
  name: string;
  bearer: string | null;
};

interface AuthContextValue {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  user: User | null;
}

type AuthContextProviderProps = {
  children: React.ReactNode;
};

const AuthContext = createContext<AuthContextValue>({
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  isAuthenticated: false,
  user: null,
});

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const queryClient = useQueryClient();
  const { instance, accounts } = useMsal();
  const [msalIdToken, setMsalIdToken] = useState<string | null>(null);

  const [primaryAccount, setPrimaryAccountState] = useState<AccountInfo | null>(() => {
    const u = localStorage.getItem('primaryAccount');
    // console.log('Primary account from local storage: ', u);
    if (u) {
      return JSON.parse(u);
    }
    return null;
  });
  const setPrimaryAccount = useCallback((u: AccountInfo | null) => {
    localStorage.setItem('primaryaccount', JSON.stringify(u));
    setPrimaryAccountState(u);
  }, []);

  useEffect(() => {
    // console.log('MSAL Id Token changed', msalIdToken);
    queryClient.invalidateQueries();
  }, [msalIdToken, queryClient]);

  const loginResponse = (response: AuthenticationResult) => {
    if (response.idToken == msalIdToken) {
      throw new Error('Id token not renewed');
    }
    if (response.idToken != msalIdToken) {
      const j = jwtDecode(response.idToken);
      if (j.exp) {
        const exp = new Date(+j.exp * 1000);
        if (exp < new Date()) {
          throw new Error('Id token from msal expired');
        }
      }
      // console.log('About to set msalid token to ...', response.idToken);
      setMsalIdToken(response.idToken);
    }
  };

  const login = useCallback<() => Promise<void>>(async () => {
    const refreshToken = async () => {
      const msalRequest = {
        scopes: ['openid', 'profile', 'User.Read'],
        account: primaryAccount || undefined,
        forceRefresh: true,
      };
      try {
        // Try to acquire token silently
        const response = await instance.acquireTokenSilent(msalRequest);
        loginResponse(response);
      } catch (error) {
        console.log('============================== error ======================================');
        if (error instanceof InteractionRequiredAuthError) {
          // call acquireTokenPopup in case of acquireTokenSilent failure due to interaction required
          console.log('interaction required');
          try {
            const newResponse = await instance.loginPopup({
              prompt: 'select_account',
              scopes: ['user.read'],
            });
            //const responseInteractive = await instance.acquireTokenPopup(msalRequest);
            loginResponse(newResponse);
          } catch (error) {
            console.log(
              '============================== interactive error ======================================'
            );
            console.log(error);
          }
        } else {
          //toast('AUTH ERROR');
          console.log(error);
        }
      }
    };
    if (primaryAccount) {
      await refreshToken();
    } else {
      await instance.loginPopup({
        prompt: 'select_account',
        scopes: ['user.read'],
      });
    }
  }, [primaryAccount, instance, msalIdToken]); 

  //useEffect(() => {
  //  let timer: number | null = null;
  //  if (msalIdToken) {
  //    const j = jwtDecode(msalIdToken);
  //    if (j.exp) {
  //      const exp = new Date(+j.exp * 1000);
  //      if (exp < new Date()) {
  //        login();
  //        //setMsalIdToken(null);
  //      } else {
  //        timer = setTimeout(
  //          () => {
  //            login();
  //            //setMsalIdToken(null);
  //          },
  //          +j.exp * 1000 - Date.now()
  //        );
  //      }
  //    }
  //    return () => {
  //      if (timer) {
  //        clearTimeout(timer);
  //      }
  //    };
  //  }
  //}, [msalIdToken, login]);

  useEffect(() => {
    // console.log('primary account changed', primaryAccount);
    if (primaryAccount?.idToken) {
      // console.log('About to set msalid token to (2)...', primaryAccount.idToken);
      setMsalIdToken(primaryAccount.idToken);
    } else {
      // console.log('About to set msalid token to (3)... null');
      setMsalIdToken(null);
    }
  }, [primaryAccount]);

  useEffect(() => {
    // Set the primary account if its currently null, and we have at least one account
    if (primaryAccount === null && accounts.length > 0) {
      setPrimaryAccount(accounts[0]);
    }
    // Remove the primary account if it is no longer in the list
    if (primaryAccount) {
      if (accounts.filter((v) => v.username === primaryAccount.username).length < 1) {
        setPrimaryAccount(null);
      }
    }
  }, [primaryAccount, accounts, setPrimaryAccount]);

  const logout = async () => {
    //setPrimaryAccount(null);
    instance.logoutPopup({ account: primaryAccount });
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        isAuthenticated: primaryAccount !== null && msalIdToken !== null,
        user: {
          email: primaryAccount?.username ? primaryAccount.username : '',
          name: primaryAccount?.name ? primaryAccount.name : '',
          bearer: msalIdToken,
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};
